import React from 'react'
import { VariantOptionProps, VLPOption } from '@storefronts/common-types'
import { imageUrlTransform } from '../../../utils/imageUrlTransform'
import { priceFormat } from '../../../utils/priceFormat'
import { TextVariantOption } from './TextVariantOption'
import {
    VariantImageContainer,
    VariantImageWrapper,
    ImageTab,
    VariantPriceContainer,
    VariantColorName,
    VariantPrice,
    VariantComparePrice,
    VariantPriceInfo,
} from '../../styled/variantComponents'

interface ColorVariantOptionProps
    extends Pick<
        VariantOptionProps,
        'value' | 'isSelected' | 'isAvailable' | 'onClick'
    > {
    option: Pick<VLPOption, 'name' | 'values'>
    showVariantImages: boolean
    getVariantImage: () => string | null
    getVariantPrice: () => {
        price: number
        compareAtPrice?: number | null
        hasDiscount: boolean
    } | null
    isClickable?: boolean
}

export const ColorVariantOption: React.FC<ColorVariantOptionProps> = ({
    option,
    value,
    isSelected: isActive,
    isAvailable,
    showVariantImages,
    getVariantImage,
    getVariantPrice,
    isClickable = true,
    onClick: onSelect,
}) => {
    const rawImage = showVariantImages ? getVariantImage() : null
    const variantImage = rawImage
        ? imageUrlTransform(rawImage, 'thumbnail')
        : null

    const priceInfo = getVariantPrice()

    const priceInfoDisplay = (
        <VariantPriceContainer>
            <VariantColorName isActive={isActive}>{value}</VariantColorName>
            {priceInfo && (
                <VariantPriceInfo>
                    <VariantPrice isActive={isActive}>
                        {priceFormat(priceInfo.price)}
                    </VariantPrice>
                    {priceInfo.compareAtPrice && priceInfo.hasDiscount && (
                        <VariantComparePrice>
                            {priceFormat(priceInfo.compareAtPrice)}
                        </VariantComparePrice>
                    )}
                </VariantPriceInfo>
            )}
        </VariantPriceContainer>
    )

    if (showVariantImages && variantImage) {
        return (
            <VariantImageContainer
                isActive={isActive}
                isAvailable={isAvailable}
                isClickable={isClickable}
                onClick={onSelect}
                role="radio"
                aria-checked={isActive}
                aria-disabled={!isClickable}
                aria-label={`${option.name}: ${value}`}
                data-testid={`color-variant-${value}`}
            >
                <VariantImageWrapper>
                    <ImageTab
                        imageUrl={variantImage}
                        active={false}
                        available={isAvailable}
                        isClickable={isClickable}
                    />
                </VariantImageWrapper>
                {priceInfoDisplay}
            </VariantImageContainer>
        )
    }

    return (
        <TextVariantOption
            value={value}
            isSelected={isActive}
            isAvailable={isAvailable}
            isClickable={isClickable}
            onClick={onSelect}
        />
    )
}
